import agwaSearchPickerStyles from './agwa-search-picker.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import type SlInput from '@shoelace-style/shoelace/dist/components/input/input.js';
import type { SearchResultRow } from '../../api/agwa-api-model';
import {
  getEntry,
  putEntry,
  requestResolve,
  subscribe,
  unsubscribe,
} from './search-resolve-cache';
import './agwa-search-dialog';
import type { SearchDialogSelection } from './agwa-search-dialog';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';

export interface SearchPickerSelection {
  id: string;
  displayValue: string;
}

/**
 * Generic entity search combo-input. Backed by the server-side search handler
 * registry. The result dialog is delegated to the reusable
 * <agwa-search-dialog>; this component owns the combo input, lazy display-text
 * resolution (via /api/search/resolve, kept fresh by an SSE subscription on the
 * tag's search topic), and selection wiring.
 *
 * The `value` prop is an entity GUID; the component resolves its own display
 * text from the resolve cache.
 *
 * States:
 *  - No value: search-icon prefix, placeholder text
 *  - Resolving: hourglass prefix, resolving placeholder
 *  - Resolved: green check prefix, entity display text
 *  - Failed: red warning prefix, error placeholder
 *
 * Events:
 *  - selection-changed — detail: SearchPickerSelection
 */
@customElement('agwa-search-picker')
export class AgwaSearchPicker extends LitElement {
  /** The server-side handler tag (e.g. "product"). */
  @property({ type: String }) tag = '';

  /** Currently selected item GUID (controlled externally). */
  @property({ type: String }) value = '';

  /** Label rendered above the input. */
  @property({ type: String }) label = '';

  /** Placeholder shown when no value is selected. */
  @property({ type: String }) placeholder = 'Search…';

  /** When true the input is read-only; no edit mode, no search button. */
  @property({ type: Boolean }) readonly = false;

  /**
   * Engine-interpreted scoping dictionary forwarded to the search dialog (and
   * on to the search request). Keys are defined by the target handler; the
   * picker does not interpret them.
   */
  @property({ attribute: false }) context?: Record<string, string>;

  // _resolving tracks whether a cache fetch is in flight for the current value.
  @state() private _resolving = false;
  @state() private _editMode = false;
  @state() private _editText = '';

  @state() private _dialogOpen = false;
  @state() private _dialogInitialText = '';

  // Polite live-region text announced to screen readers when resolve state changes.
  @state() private _resolveAnnouncement = '';

  private readonly _cacheListener = () => this._onCacheUpdate();
  private _subscribedTag = '';
  private _subscribedValue = '';
  static styles = unsafeCSS(agwaSearchPickerStyles);

  // ── Lifecycle ────────────────────────────────────────────────────────────

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._detach();
  }

  override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('tag') || changedProperties.has('value')) {
      this._editMode = false;
      this._editText = '';
      this._detach();
      if (this.tag && this.value) {
        this._attach(this.tag, this.value);
      } else {
        this._resolving = false;
      }
    }
  }

  // ── Cache subscription ────────────────────────────────────────────────────

  private _attach(tag: string, val: string) {
    if (this._subscribedTag === tag && this._subscribedValue === val) return;
    this._subscribedTag = tag;
    this._subscribedValue = val;
    subscribe(tag, val, this._cacheListener);
    if (getEntry(tag, val)) {
      this._resolving = false;
    } else {
      this._resolving = true;
      void requestResolve(tag, val);
    }
  }

  private _detach() {
    if (this._subscribedTag && this._subscribedValue) {
      unsubscribe(
        this._subscribedTag,
        this._subscribedValue,
        this._cacheListener
      );
      this._subscribedTag = '';
      this._subscribedValue = '';
    }
  }

  private _onCacheUpdate() {
    this._resolving = false;
    if (this._resolvedRow) {
      this._resolveAnnouncement = `Selected: ${this._resolvedRow.displayValue}`;
    } else if (this._hasValue) {
      this._resolveAnnouncement = 'Selection unavailable.';
    } else {
      this._resolveAnnouncement = '';
    }
    this.requestUpdate();
  }

  private get _resolvedRow(): SearchResultRow | undefined {
    if (!this.tag || !this.value) return undefined;
    return getEntry(this.tag, this.value);
  }

  // ── Resolve state (computed, not stored) ─────────────────────────────────

  private get _hasValue(): boolean {
    return !!(this.tag && this.value);
  }

  private get _isResolved(): boolean {
    return this._hasValue && !!getEntry(this.tag, this.value);
  }

  // ── Input state derivations ───────────────────────────────────────────────

  private get _inputValue(): string {
    if (this._editMode) return this._editText;
    return this._resolvedRow?.displayValue ?? '';
  }

  private get _inputPlaceholder(): string {
    if (this._editMode) return 'Type to search... esc to cancel';
    if (this._hasValue && this._resolving) return 'Resolving…';
    return this.placeholder;
  }

  private get _prefixIcon(): string {
    if (this._editMode) return 'question-circle';
    if (!this._hasValue) return 'question-circle';
    if (this._resolving && !this._isResolved) return 'hourglass-split';
    if (this._isResolved) return 'check-circle';
    return 'exclamation-circle';
  }

  private get _prefixColor(): string {
    if (this._editMode) return 'var(--sl-color-neutral-400)';
    if (!this._hasValue) return 'var(--sl-color-neutral-400)';
    if (this._resolving && !this._isResolved)
      return 'var(--sl-color-warning-500)';
    if (this._isResolved) return 'var(--sl-color-success-600)';
    return 'var(--sl-color-danger-600)';
  }

  // ── Input interaction ─────────────────────────────────────────────────────

  private _onInputClick() {
    if (this.readonly || this._editMode) return;
    this._enterEditMode();
  }

  private _enterEditMode(initialText = '') {
    this._editText = initialText;
    this._editMode = true;
    void this.updateComplete.then(() => {
      const input = this.shadowRoot?.querySelector<SlInput>(
        'sl-input.picker-input'
      );
      if (!input) return;
      input.focus();
      if (initialText) {
        const native =
          input.shadowRoot?.querySelector<HTMLInputElement>('input');
        if (native)
          native.setSelectionRange(initialText.length, initialText.length);
      }
    });
  }

  private _onTextInput(e: CustomEvent) {
    const target = e.currentTarget as HTMLInputElement;
    const text = target.value ?? '';
    if (!this._editMode) {
      this._editMode = true;
    }
    this._editText = text;
  }

  private _onKeyDown(e: KeyboardEvent) {
    if (this.readonly) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      this._openDialog(this._editMode ? this._editText : '');
    } else if (e.key === 'Escape') {
      // Cancelled in both modes: the input is type="search", and Chromium clears a search input on
      // an uncancelled Escape, which in view mode would blank the chosen name and fall into edit
      // mode. Escape means "cancel the edit" here, so it only leaves edit mode.
      e.preventDefault();
      if (this._editMode) this._exitEditMode();
    } else if (
      !this._editMode &&
      e.key.length === 1 &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      this._enterEditMode(e.key);
    }
  }

  private _onBlur() {
    if (!this._editMode) return;
    // Small delay so click events on the suffix button fire before this runs
    setTimeout(() => {
      if (this._editMode && !this._dialogOpen) {
        this._exitEditMode();
      }
    }, 150);
  }

  private _exitEditMode() {
    this._editMode = false;
    this._editText = '';
  }

  private _onSuffixMouseDown(e: Event) {
    e.preventDefault();
  }

  private _onSuffixClick(e: Event) {
    e.stopPropagation();
    if (this.readonly) return;
    this._openDialog(this._editMode ? this._editText : '');
  }

  // ── Dialog ────────────────────────────────────────────────────────────────

  private _openDialog(initialText = '') {
    this._dialogInitialText = initialText;
    this._dialogOpen = true;
  }

  private _closeDialog() {
    this._dialogOpen = false;
    this._exitEditMode();
  }

  private _onDialogSelect(e: CustomEvent<SearchDialogSelection>) {
    const { id, displayValue, row } = e.detail;
    putEntry(this.tag, id, row);
    this._dialogOpen = false;
    this._editMode = false;
    this._editText = '';
    this.dispatchEvent(
      new CustomEvent<SearchPickerSelection>('selection-changed', {
        detail: { id, displayValue },
        bubbles: true,
        composed: true,
      })
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  render() {
    return html`
      ${this._renderComboInput()}
      <agwa-search-dialog
        .tag=${this.tag}
        .label=${this.label}
        .open=${this._dialogOpen}
        .initialText=${this._dialogInitialText}
        .context=${this.context}
        @select=${this._onDialogSelect}
        @dialog-hide=${this._closeDialog}
      ></agwa-search-dialog>
      <div class="sr-only" aria-live="polite" aria-atomic="true">
        ${this._resolveAnnouncement}
      </div>
    `;
  }

  private _renderComboInput() {
    return html`
      <!-- type="search": a search box is not a credential field, which is what keeps Safari and password managers from offering saved logins here (FORM-AUTOFILL; data-entry-autofill.md). -->
      <sl-input
        type="search"
        class=${`picker-input${this._editMode ? ' editing' : ''}`}
        label=${ifDefined(this.label || undefined)}
        placeholder=${this._inputPlaceholder}
        .value=${live(this._inputValue)}
        ?readonly=${this.readonly}
        autocomplete="off"
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded=${this._dialogOpen ? 'true' : 'false'}
        aria-autocomplete="list"
        @click=${this._onInputClick}
        @sl-input=${this._onTextInput}
        @keydown=${this._onKeyDown}
        @sl-blur=${this._onBlur}
      >
        <sl-icon
          slot="prefix"
          name=${this._prefixIcon}
          style="color: ${this._prefixColor}; font-size: 1rem;"
        ></sl-icon>
        ${
          !this.readonly
            ? html`
                <sl-icon-button
                  slot="suffix"
                  name="search"
                  label="Open search"
                  @mousedown=${this._onSuffixMouseDown}
                  @click=${this._onSuffixClick}
                ></sl-icon-button>
              `
            : nothing
        }
        ${
          this._editMode && this._resolvedRow
            ? html`
                <span slot="help-text" class="picker-hint">
                  <sl-icon name="check-circle"></sl-icon>
                  <span class="picker-hint-label">Current</span>
                  <span class="picker-hint-value"
                    >${this._resolvedRow.displayValue}</span
                  >
                </span>
              `
            : nothing
        }
      </sl-input>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-search-picker': AgwaSearchPicker;
  }
}
